import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/components/Toast';
import { Receipt as ReceiptIcon, Plus, Save, Printer, Trash2, Loader2, X } from 'lucide-react';

const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const safe = (v) => (v == null ? '' : String(v).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])));

// Conversão de número para extenso (português brasileiro)
function numeroPorExtenso(valor) {
  const v = Number(valor || 0);
  if (v === 0) return 'zero reais';
  const reais = Math.floor(v);
  const cents = Math.round((v - reais) * 100);
  let txt = '';
  if (reais > 0) txt = numToExt(reais) + (reais === 1 ? ' real' : ' reais');
  if (cents > 0) txt += (txt ? ' e ' : '') + numToExt(cents) + (cents === 1 ? ' centavo' : ' centavos');
  return txt;
}
function numToExt(n) {
  if (n === 0) return '';
  const u = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
  const d10 = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const d = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const c = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];
  if (n < 10) return u[n];
  if (n < 20) return d10[n - 10];
  if (n < 100) return d[Math.floor(n / 10)] + (n % 10 ? ' e ' + u[n % 10] : '');
  if (n === 100) return 'cem';
  if (n < 1000) {
    return c[Math.floor(n / 100)] + (n % 100 ? ' e ' + numToExt(n % 100) : '');
  }
  if (n < 1000000) {
    const milhar = Math.floor(n / 1000);
    const resto = n % 1000;
    const milharTxt = milhar === 1 ? 'mil' : numToExt(milhar) + ' mil';
    return milharTxt + (resto ? (resto < 100 ? ' e ' : ' ') + numToExt(resto) : '');
  }
  if (n < 1000000000) {
    const mi = Math.floor(n / 1000000);
    const resto = n % 1000000;
    const miTxt = mi === 1 ? 'um milhão' : numToExt(mi) + ' milhões';
    return miTxt + (resto ? ' ' + numToExt(resto) : '');
  }
  return String(n);
}

export default function ReceiptsPage() {
  const toast = useToast();
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [company, setCompany] = useState({});
  const [tenant, setTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const blank = useCallback(() => ({
    id: null,
    payer_customer_id: '',
    payer_name: '',
    payer_document: '',
    payer_address: '',
    amount: 0,
    description: '',
    payment_method: 'PIX',
    related_proposal_id: '',
    city: company?.cidade || '',
    issued_at: new Date().toISOString().slice(0, 10),
    notes: '',
  }), [company]);

  const [form, setForm] = useState(blank());

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, cRes, pRes, coRes, tRes] = await Promise.all([
        api.get('/receipts').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] })),
        api.get('/proposals').catch(() => ({ data: [] })),
        api.get('/settings/company').catch(() => ({ data: {} })),
        api.get('/auth/me').catch(() => ({ data: {} })),
      ]);
      setReceipts(Array.isArray(rRes.data) ? rRes.data : []);
      setCustomers(Array.isArray(cRes.data) ? cRes.data : (cRes.data?.customers || []));
      setProposals(Array.isArray(pRes.data) ? pRes.data : []);
      setCompany(coRes.data || {});
      setTenant(tRes.data || {});
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => {
    setForm(blank());
    setShowForm(true);
  };

  const handleCustomerSelect = (id) => {
    const c = customers.find(x => x.id === id);
    setForm(f => ({
      ...f,
      payer_customer_id: id,
      payer_name: c?.name || f.payer_name,
      payer_document: c?.cpf_cnpj || f.payer_document,
      payer_address: c?.address || f.payer_address,
    }));
  };

  const handleProposalSelect = (id) => {
    const p = proposals.find(x => x.id === id);
    if (!p) { setForm(f => ({ ...f, related_proposal_id: '' })); return; }
    const total = (p.items || []).reduce(
      (s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0) - Number(it.discount || 0)), 0
    );
    setForm(f => ({
      ...f,
      related_proposal_id: id,
      amount: total,
      description: f.description || `Referente à proposta "${p.title}"`,
      payer_customer_id: p.customer_id || f.payer_customer_id,
      payer_name: f.payer_name || p.customer_snapshot?.name || '',
      payer_document: f.payer_document || p.customer_snapshot?.cpf_cnpj || '',
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este recibo?')) return;
    try { await api.delete(`/receipts/${id}`); toast.success('Recibo excluído'); loadAll(); }
    catch { toast.error('Erro ao excluir'); }
  };

  const handleSave = async () => {
    if (!form.payer_name.trim()) { toast.error('Informe o nome do pagador'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Valor inválido'); return; }
    if (!form.description.trim()) { toast.error('Informe a descrição (referente a)'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        issued_at: form.issued_at ? new Date(form.issued_at).toISOString() : new Date().toISOString(),
      };
      const { data } = await api.post('/receipts', payload);
      toast.success(`Recibo #${data.number} criado com sucesso`);
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = (receipt) => {
    const issuedDate = receipt.issued_at ? new Date(receipt.issued_at) : new Date();
    const dateStr = issuedDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    const valorExt = numeroPorExtenso(receipt.amount);
    const logo = tenant?.logo || company?.logo || '';
    const number = String(receipt.number || '').padStart(6, '0');

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>Recibo Nº ${number}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Georgia', 'Times New Roman', serif; color: #111; margin: 18mm; font-size: 13px; line-height: 1.6; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding-bottom: 12px; border-bottom: 2px solid #333; }
  .header img { max-height: 70px; max-width: 180px; object-fit: contain; }
  h1 { font-size: 22px; margin: 0; letter-spacing: 1px; }
  .meta { font-size: 11px; color: #444; }
  .receipt-box {
    margin-top: 30px;
    border: 2px solid #000;
    padding: 24px;
    border-radius: 6px;
    background: #fcfcfc;
    min-height: 320px;
  }
  .title-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 16px; }
  .title-row h2 { font-size: 28px; margin: 0; letter-spacing: 4px; }
  .number { font-size: 14px; font-weight: 700; }
  .amount-bar {
    display: inline-block;
    border: 1px solid #000;
    padding: 8px 18px;
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 16px;
    background: #fff;
  }
  .body p { text-align: justify; line-height: 1.8; margin: 8px 0; }
  .body p strong { text-decoration: underline; }
  .signature {
    margin-top: 70px;
    text-align: center;
  }
  .signature .line {
    margin: 0 auto;
    width: 60%;
    border-top: 1px solid #000;
    padding-top: 6px;
    font-size: 11px;
  }
  .footer { margin-top: 16px; font-size: 10px; color: #888; text-align: center; }
  @media print { body { margin: 12mm; } .no-print { display: none !important; } }
</style></head>
<body>
  <div class="header">
    <div>
      ${logo ? `<img src="${safe(logo)}" alt="Logo"/><br/>` : ''}
      <h1>${safe((company.razao_social || company.nome_fantasia || 'EMPRESA').toUpperCase())}</h1>
      <div class="meta">
        ${company.cnpj ? `CNPJ: ${safe(company.cnpj)}` : ''} ${company.inscricao_estadual ? ` | IE: ${safe(company.inscricao_estadual)}` : ''}<br/>
        ${[company.endereco || company.logradouro, company.numero ? 'nº ' + company.numero : '', company.bairro].filter(Boolean).map(safe).join(', ')}<br/>
        ${[company.cidade, company.uf].filter(Boolean).map(safe).join(' - ')} ${company.cep ? 'CEP ' + safe(company.cep) : ''}<br/>
        ${company.telefone ? 'Tel: ' + safe(company.telefone) : ''} ${company.email ? ' | ' + safe(company.email) : ''}
      </div>
    </div>
    <div style="text-align:right">
      <div class="meta">Recibo Nº</div>
      <div style="font-size:18px;font-weight:700">${number}</div>
      <div class="meta">${safe(dateStr)}</div>
    </div>
  </div>

  <div class="receipt-box">
    <div class="title-row">
      <h2>RECIBO</h2>
      <div class="number">Nº ${number}</div>
    </div>
    <div class="amount-bar">${brl(receipt.amount)}</div>

    <div class="body">
      <p>
        Recebi(emos) de <strong>${safe(receipt.payer_name)}</strong>${receipt.payer_document ? `, portador(a) do documento <strong>${safe(receipt.payer_document)}</strong>` : ''}${receipt.payer_address ? `, residente em ${safe(receipt.payer_address)}` : ''},
        a importância de <strong>${brl(receipt.amount)}</strong> (${valorExt}),
        referente a <strong>${safe(receipt.description)}</strong>${receipt.payment_method ? `, pago(s) via ${safe(receipt.payment_method)}` : ''}.
      </p>
      <p>Para maior clareza firmo o presente recibo dando plena, geral e irrevogável quitação pelo valor recebido.</p>
      ${receipt.notes ? `<p style="font-style:italic">Observações: ${safe(receipt.notes)}</p>` : ''}
    </div>

    <div class="signature">
      <div style="margin-bottom:6px">${safe(receipt.city || company.cidade || '')}${receipt.city || company.cidade ? ', ' : ''}${safe(dateStr)}</div>
      <br/><br/>
      <div class="line">${safe((company.razao_social || company.nome_fantasia || 'EMITENTE'))}</div>
      ${company.cnpj ? `<div style="font-size:11px;margin-top:2px">CNPJ: ${safe(company.cnpj)}</div>` : ''}
    </div>
  </div>

  <div class="footer">Documento gerado em ${new Date().toLocaleString('pt-BR')}</div>

  <script>window.addEventListener('load', () => setTimeout(() => window.print(), 250));</script>
</body></html>`;

    const w = window.open('', '_blank', 'width=900,height=900');
    if (!w) { toast.error('Pop-up bloqueado. Permita pop-ups deste site.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-6" data-testid="receipts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ReceiptIcon className="w-6 h-6 text-emerald-600" /> Recibos de Pagamento</h1>
          <p className="text-sm text-slate-500">Emita recibos de pagamento com valor por extenso</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-receipt-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo recibo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recibos emitidos</CardTitle>
          <CardDescription>{receipts.length} recibo(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {receipts.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <ReceiptIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              Nenhum recibo emitido. Clique em "Novo recibo" para começar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº</TableHead>
                  <TableHead>Pagador</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Referente a</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipts.map(r => (
                  <TableRow key={r.id} data-testid={`receipt-row-${r.id}`}>
                    <TableCell className="font-mono">#{String(r.number || '').padStart(6, '0')}</TableCell>
                    <TableCell className="font-medium">{r.payer_name}</TableCell>
                    <TableCell>{r.payer_document || '—'}</TableCell>
                    <TableCell className="font-semibold text-emerald-700">{brl(r.amount)}</TableCell>
                    <TableCell className="max-w-xs truncate" title={r.description}>{r.description}</TableCell>
                    <TableCell>{r.issued_at ? new Date(r.issued_at).toLocaleDateString('pt-BR') : '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handlePrint(r)} title="Imprimir / PDF" data-testid={`print-receipt-${r.id}`}>
                          <Printer className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} title="Excluir" data-testid={`delete-receipt-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-rose-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-auto p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full my-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Novo recibo</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Pagador */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <h4 className="font-semibold mb-2 text-sm">Pagador</h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Cliente cadastrado (opcional)</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={form.payer_customer_id}
                      onChange={(e) => handleCustomerSelect(e.target.value)}
                      data-testid="receipt-customer-select"
                    >
                      <option value="">— Digitar manualmente —</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name} {c.cpf_cnpj ? `(${c.cpf_cnpj})` : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Nome do pagador *</Label>
                    <Input value={form.payer_name} onChange={(e) => setForm({ ...form, payer_name: e.target.value })} data-testid="receipt-payer-name" />
                  </div>
                  <div>
                    <Label>CPF / CNPJ</Label>
                    <Input value={form.payer_document} onChange={(e) => setForm({ ...form, payer_document: e.target.value })} placeholder="000.000.000-00" />
                  </div>
                  <div>
                    <Label>Endereço</Label>
                    <Input value={form.payer_address} onChange={(e) => setForm({ ...form, payer_address: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Vincular a contrato/proposta */}
              {proposals.length > 0 && (
                <div>
                  <Label>Vincular a um contrato/proposta (opcional)</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.related_proposal_id}
                    onChange={(e) => handleProposalSelect(e.target.value)}
                    data-testid="receipt-proposal-select"
                  >
                    <option value="">— Não vincular —</option>
                    {proposals.map(p => {
                      const total = (p.items || []).reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.unit_price || 0) - Number(it.discount || 0)), 0);
                      return <option key={p.id} value={p.id}>{p.title} — {brl(total)}</option>;
                    })}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">Selecionando um contrato, o valor e descrição são preenchidos automaticamente.</p>
                </div>
              )}

              {/* Valor + descrição */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Valor (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
                    data-testid="receipt-amount-input"
                  />
                  {form.amount > 0 && (
                    <p className="text-xs text-emerald-700 mt-1 italic">{numeroPorExtenso(form.amount)}</p>
                  )}
                </div>
                <div>
                  <Label>Forma de pagamento</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  >
                    <option>PIX</option>
                    <option>Dinheiro</option>
                    <option>Cartão de Crédito</option>
                    <option>Cartão de Débito</option>
                    <option>Transferência bancária</option>
                    <option>Boleto</option>
                    <option>Cheque</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Referente a *</Label>
                <Textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ex: prestação de serviços de implantação do sistema PDV — mês 02/2026"
                  data-testid="receipt-description"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Cidade</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Cidade onde o recibo é emitido" />
                </div>
                <div>
                  <Label>Data de emissão</Label>
                  <Input type="date" value={form.issued_at} onChange={(e) => setForm({ ...form, issued_at: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Observações (opcional)</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-receipt-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Emitir recibo
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
